import {
  type Entity,
  type LineEntity,
  Vec2,
  chamferLines,
  entityBBox,
  extendEntity,
  filletLines,
  mirrorEntityAcross,
  offsetEntity,
  trimEntity,
} from '@lakaran/geometry';
import { Transaction } from '@lakaran/engine';
import { Tool, type ToolField, type ToolHost } from './tool';

/** Cutters must cross the target, so its own box is enough (and cheap). */
function cuttersFor(host: ToolHost, target: Entity): Entity[] {
  return host.doc.queryVisible(entityBBox(target).expand(1e-6));
}

/** Boundaries can be outside the target, so widen to the view plus a margin. */
function boundariesFor(host: ToolHost, target: Entity): Entity[] {
  const view = host.editor.viewport.bounds;
  const box = entityBBox(target);
  const pad = Math.max(box.width, box.height, 1) * 2;
  return host.doc.queryVisible(view.union(box.expand(pad)));
}

export class TrimTool extends Tool {
  readonly name = 'TRIM';
  readonly title = 'Trim';
  readonly aliases = ['trim', 'tr'];

  protected begin(): void {
    this.host.prompt('TRIM  Select the part of an object to trim:');
  }

  point(p: Vec2): void {
    const editor = this.host.editor;
    const target = this.host.doc.get(editor.pickAt(p) ?? '');
    if (!target) return;

    const pieces = trimEntity(target, cuttersFor(this.host, target), p);
    if (!pieces) {
      editor.message('TRIM  Nothing to trim at that pick.');
      return;
    }

    const tx = new Transaction().remove(target);
    for (const piece of pieces) tx.add(piece);
    editor.commit(tx, 'TRIM');
  }
}

export class ExtendTool extends Tool {
  readonly name = 'EXTEND';
  readonly title = 'Extend';
  readonly aliases = ['extend', 'ex'];

  protected begin(): void {
    this.host.prompt('EXTEND  Select the end of an object to extend:');
  }

  point(p: Vec2): void {
    const editor = this.host.editor;
    const target = this.host.doc.get(editor.pickAt(p) ?? '');
    if (!target) return;

    const extended = extendEntity(target, boundariesFor(this.host, target), p);
    if (!extended) {
      editor.message('EXTEND  No boundary in that direction.');
      return;
    }

    editor.commit(new Transaction().update(target, extended), 'EXTEND');
  }
}

export class OffsetTool extends Tool {
  readonly name = 'OFFSET';
  readonly title = 'Offset';
  readonly aliases = ['offset', 'o'];

  private distance = 5;
  private settingDistance = true;
  private source?: Entity;

  protected begin(): void {
    this.host.prompt('OFFSET  Specify offset distance:');
  }

  override getFields(): ToolField[] | null {
    if (!this.settingDistance) return null;
    return [
      {
        id: 'distance',
        label: 'Distance',
        unit: 'mm',
        value: this.distance,
        decimals: 3,
        min: 0.0001,
      },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    const distance = values['distance'] ?? this.distance;
    if (!(distance > 0)) {
      this.host.editor.message('OFFSET  Distance must be greater than zero.');
      return;
    }
    this.distance = distance;
    this.settingDistance = false;
    this.host.prompt('OFFSET  Select object to offset [Enter to finish]:');
  }

  point(p: Vec2): void {
    const editor = this.host.editor;
    if (this.settingDistance) return;

    if (!this.source) {
      const id = editor.pickAt(p);
      const entity = id ? this.host.doc.get(id) : undefined;
      if (!entity) return;
      this.source = entity;
      this.host.setReference(p);
      this.host.prompt(`OFFSET  Specify side to offset (${this.distance} mm):`);
      return;
    }

    const result = offsetEntity(this.source, this.distance, p);
    if (!result) {
      editor.message('OFFSET  That offset is not possible for this object.');
      this.host.endTool();
      return;
    }

    editor.commit(new Transaction().add(result), 'OFFSET');
    this.source = undefined;
    this.host.setDraft([]);
    this.host.prompt('OFFSET  Select object to offset [Enter to finish]:');
  }

  override hover(p: Vec2): void {
    if (this.settingDistance || !this.source) return;
    const result = offsetEntity(this.source, this.distance, p);
    this.host.setDraft(result ? [result] : []);
  }

  override cancel(): void {
    this.source = undefined;
    this.host.setDraft([]);
    super.cancel();
  }
}

export class MirrorTool extends Tool {
  readonly name = 'MIRROR';
  readonly title = 'Mirror';
  readonly aliases = ['mirror', 'mi'];

  private axisStart?: Vec2;
  private selecting = false;

  protected begin(): void {
    if (this.host.editor.selection.size === 0) {
      this.selecting = true;
      this.host.prompt('MIRROR  Select objects [Enter to continue]:');
    } else {
      this.host.prompt('MIRROR  Specify first point of mirror line:');
    }
  }

  override get referencePoint(): Vec2 | undefined {
    return this.axisStart;
  }

  point(p: Vec2): void {
    if (this.selecting) {
      const id = this.host.editor.pickAt(p);
      if (id) this.host.editor.toggleSelection(id);
      return;
    }
    if (!this.axisStart) {
      this.axisStart = p;
      this.host.setReference(p);
      this.host.prompt('MIRROR  Specify second point of mirror line:');
      return;
    }
    if (this.axisStart.distanceTo(p) < 1e-9) return;

    const tx = new Transaction();
    for (const entity of this.host.editor.selectedEntities()) {
      tx.add(mirrorEntityAcross(entity, this.axisStart, p));
    }
    if (!tx.isEmpty) this.host.editor.commit(tx, 'MIRROR');
    this.host.endTool();
  }

  override enter(): void {
    if (this.selecting) {
      this.selecting = false;
      this.host.prompt('MIRROR  Specify first point of mirror line:');
      return;
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.axisStart) return;
    if (this.axisStart.distanceTo(p) < 1e-9) return;
    this.host.setDraft(
      this.host.editor
        .selectedEntities()
        .map((entity) => mirrorEntityAcross(entity, this.axisStart!, p)),
    );
  }

  override cancel(): void {
    this.axisStart = undefined;
    this.selecting = false;
    super.cancel();
  }
}

export class FilletTool extends Tool {
  readonly name = 'FILLET';
  readonly title = 'Fillet';
  readonly aliases = ['fillet', 'f'];

  private radius = 5;
  private first?: { entity: LineEntity; pick: Vec2 };

  protected begin(): void {
    this.host.prompt(`FILLET  Select the first line (radius ${this.radius} mm):`);
  }

  override getFields(): ToolField[] {
    return [
      { id: 'radius', label: 'Radius', unit: 'mm', value: this.radius, decimals: 3, min: 0.0001 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    const radius = values['radius'] ?? this.radius;
    if (!(radius > 0)) {
      this.host.message('FILLET  Radius must be greater than zero.');
      return;
    }
    this.radius = radius;
    if (!this.first) this.host.prompt(`FILLET  Select the first line (radius ${radius} mm):`);
  }

  /** The line under the cursor, or a hint when it is not a line. */
  private lineAt(p: Vec2): LineEntity | null {
    const id = this.host.editor.pickAt(p);
    const entity = id ? this.host.doc.get(id) : undefined;
    if (!entity) return null;
    if (entity.type !== 'line') {
      this.host.message('FILLET  Fillet works between two straight lines.');
      return null;
    }
    return entity;
  }

  point(p: Vec2): void {
    const editor = this.host.editor;
    const line = this.lineAt(p);
    if (!line) return;

    if (!this.first) {
      this.first = { entity: line, pick: p };
      this.host.setReference(p);
      this.host.prompt('FILLET  Select the second line:');
      return;
    }
    if (this.first.entity.id === line.id) return;

    const result = filletLines(this.first.entity, line, this.radius, this.first.pick, p);
    if (!result) {
      editor.message(`FILLET  No corner fits a ${this.radius} mm radius — try a smaller one.`);
      this.first = undefined;
      this.host.setDraft([]);
      this.host.prompt(`FILLET  Select the first line (radius ${this.radius} mm):`);
      return;
    }

    const tx = new Transaction().remove(this.first.entity).remove(line);
    for (const trimmed of result.lines) tx.add(trimmed);
    tx.add(result.connector);
    editor.commit(tx, 'FILLET');

    this.first = undefined;
    this.host.setDraft([]);
    this.host.prompt(`FILLET  Select the first line (radius ${this.radius} mm):`);
  }

  override hover(p: Vec2): void {
    if (!this.first) return;
    const line = this.lineAt(p);
    if (!line || line.id === this.first.entity.id) {
      this.host.setDraft([]);
      return;
    }
    const result = filletLines(this.first.entity, line, this.radius, this.first.pick, p);
    if (!result) {
      this.host.setDraft([]);
      return;
    }
    this.host.setDraft([...result.lines, result.connector]);
  }

  override cancel(): void {
    this.first = undefined;
    this.host.setDraft([]);
    super.cancel();
  }
}

export class ChamferTool extends Tool {
  readonly name = 'CHAMFER';
  readonly title = 'Chamfer';
  readonly aliases = ['chamfer', 'cha'];

  private distanceA = 5;
  private distanceB = 5;
  private first?: { entity: LineEntity; pick: Vec2 };

  protected begin(): void {
    this.host.prompt(`CHAMFER  Select the first line (${this.distanceA} × ${this.distanceB} mm):`);
  }

  override getFields(): ToolField[] {
    return [
      { id: 'distanceA', label: 'Distance 1', unit: 'mm', value: this.distanceA, decimals: 3, min: 0.0001 },
      { id: 'distanceB', label: 'Distance 2', unit: 'mm', value: this.distanceB, decimals: 3, min: 0.0001 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    const a = values['distanceA'] ?? this.distanceA;
    const b = values['distanceB'] ?? this.distanceB;
    if (!(a > 0) || !(b > 0)) {
      this.host.message('CHAMFER  Distances must be greater than zero.');
      return;
    }
    this.distanceA = a;
    this.distanceB = b;
    if (!this.first) this.host.prompt(`CHAMFER  Select the first line (${a} × ${b} mm):`);
  }

  private lineAt(p: Vec2): LineEntity | null {
    const id = this.host.editor.pickAt(p);
    const entity = id ? this.host.doc.get(id) : undefined;
    if (!entity) return null;
    if (entity.type !== 'line') {
      this.host.message('CHAMFER  Chamfer works between two straight lines.');
      return null;
    }
    return entity;
  }

  point(p: Vec2): void {
    const editor = this.host.editor;
    const line = this.lineAt(p);
    if (!line) return;

    if (!this.first) {
      this.first = { entity: line, pick: p };
      this.host.setReference(p);
      this.host.prompt('CHAMFER  Select the second line:');
      return;
    }
    if (this.first.entity.id === line.id) return;

    const result = chamferLines(
      this.first.entity,
      line,
      this.distanceA,
      this.distanceB,
      this.first.pick,
      p,
    );
    if (!result) {
      editor.message('CHAMFER  Those distances do not fit between these lines.');
      this.first = undefined;
      this.host.setDraft([]);
      this.host.prompt(`CHAMFER  Select the first line (${this.distanceA} × ${this.distanceB} mm):`);
      return;
    }

    const tx = new Transaction().remove(this.first.entity).remove(line);
    for (const trimmed of result.lines) tx.add(trimmed);
    tx.add(result.connector);
    editor.commit(tx, 'CHAMFER');

    this.first = undefined;
    this.host.setDraft([]);
    this.host.prompt(`CHAMFER  Select the first line (${this.distanceA} × ${this.distanceB} mm):`);
  }

  override hover(p: Vec2): void {
    if (!this.first) return;
    const line = this.lineAt(p);
    if (!line || line.id === this.first.entity.id) {
      this.host.setDraft([]);
      return;
    }
    const result = chamferLines(
      this.first.entity,
      line,
      this.distanceA,
      this.distanceB,
      this.first.pick,
      p,
    );
    this.host.setDraft(result ? [...result.lines, result.connector] : []);
  }

  override cancel(): void {
    this.first = undefined;
    this.host.setDraft([]);
    super.cancel();
  }
}

export function createEditTools(): Array<() => Tool> {
  return [
    () => new TrimTool(),
    () => new ExtendTool(),
    () => new FilletTool(),
    () => new ChamferTool(),
    () => new OffsetTool(),
    () => new MirrorTool(),
  ];
}
